import * as React from "react";
import { Form, Select, SelectProps } from "antd";
import { observer } from "mobx-react-lite";
import { ZIdName } from "src/types/ZIdName";
import { ZUid } from "src/types/zUid";
import { onError } from "src/common/onError";
import { mc2SettingsStore as store } from "./mc2SettingsStore";
import styles from "./Mc2Settings.module.less";
import { Mc2ObjField, ZMc2Settings } from "../Mc2Types";

interface PropsMc2Settings {
  modelObjectId: ZUid;
  loadObjectOptions(): Promise<ZIdName[]>;
}

export const Mc2Settings: React.FC<PropsMc2Settings> = observer((props) => {
  const { modelObjectId, loadObjectOptions } = props;
  React.useEffect(() => {
    store.init(modelObjectId, loadObjectOptions).catch(onError);
  }, [modelObjectId]);
  return (
    <div className={styles.box}>
      <h3 style={{ gridColumn: "span 2" }}>Measurement Chart</h3>
      <Mc2ObjSelect
        name="objMcId"
        label="Объект Measurement Chart"
        attrs={[
          { attrName: "mcAttrCommentId", label: "Комментарий" },
          { attrName: "mcAttrImgAttrId", label: "Изображение" },
          { attrName: "mcAttrBaseSizeAttrId", label: "Базовый размер" },
          {
            attrName: "mcAttrLinkToModelAttrId",
            label: "Ссылка на объект настройки БС",
          },
          { attrName: "mcAttrLinkToSizeLineId", label: "Size Line" },
        ]}
      />

      <h3>Размерный ряд</h3>
      <Mc2ObjSelect
        name="objSizeLineId"
        label="Объект размерного ряда"
        attrs={[]}
      />

      <h3>Имя размера</h3>
      <Mc2ObjSelect
        name="objSizeId"
        label="Объект имени размера"
        attrs={[
          {
            attrName: "sizeLinkToSizeLineAttrId",
            label: "Ссылка на размерный ряд",
          },
        ]}
      />

      <h3>Точка</h3>
      <Mc2ObjSelect
        name="objPointId"
        label="Объект точки"
        attrs={[{ attrName: "pointAttrDescriptionId", label: "Описание" }]}
      />

      <div className={styles.footer} />
    </div>
  );
});

type AttrDescr = {
  attrName: keyof ZMc2Settings | null;
  label: string; // если attrName=null, то это ключ для React
};

interface PropsMc2ObjSelect {
  name: Mc2ObjField;
  label: string;
  attrs: AttrDescr[];
}

const Mc2ObjSelect: React.FC<PropsMc2ObjSelect> = observer((props) => {
  const { name: objName, label: objLabel, attrs } = props;
  return (
    <>
      <Form.Item
        name={objName}
        label={objLabel}
        style={{ gridColumn: "span 2" }}
        rules={[{ required: true }]}
      >
        <Select
          options={store.objectOptions}
          allowClear
          fieldNames={{ label: "name", value: "id" }}
          placeholder="Выбрать"
          showSearch
          filterOption={filterOptionByName}
        />
      </Form.Item>
      {attrs.map(({ attrName, label: attrLabel }) =>
        attrName ? (
          <Mc2Attr
            key={attrName}
            objName={objName}
            attrName={attrName}
            label={attrLabel}
          />
        ) : (
          <div key={attrLabel} />
        ),
      )}
    </>
  );
});

export const filterOptionByName: SelectProps<ZUid, ZIdName>["filterOption"] = (
  input,
  option,
): boolean =>
  (typeof option?.name === "string" ? option.name : "")
    .toLowerCase()
    .includes(input.toLowerCase());

interface PropsMc2Attr {
  objName: Mc2ObjField;
  attrName: keyof ZMc2Settings;
  label: string;
}

const Mc2Attr: React.FC<PropsMc2Attr> = observer((props) => {
  const { objName, attrName, label } = props;
  const form = Form.useFormInstance();
  const objId = Form.useWatch(objName, form);
  React.useEffect(() => {
    form.resetFields([attrName]);
    if (objId) {
      store.updateAttrs(objId);
    }
  }, [objId]);

  return (
    <Form.Item name={attrName} label={label} rules={[{ required: true }]}>
      <Select
        disabled={!objId}
        allowClear
        placeholder="Выбрать"
        options={store.attrDict[objId] ?? []}
        fieldNames={{ label: "name", value: "id" }}
      />
    </Form.Item>
  );
});
